import React from 'react';
import { Redirect } from 'react-router-dom';
import moment from 'moment';
import queryString from 'query-string';
import * as Yup from 'yup';
import get from 'lodash/get';
import cx from 'classnames';
import T from 'i18n-react';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';

import Typography from '@material-ui/core/Typography';
import { TextField } from 'icw-components/InputFields';
import { Form } from 'components/Form';
import { BRANDED_LOGIN_PATH, LOGIN_LANG_PATH } from 'shared/path';
import { LOCKED_LOGIN_TIME } from 'shared/constants';
import { FEDEX_PATH } from 'shared/routes';
import { nextPath2, setLanguageLocale } from 'util/icwUtility';
import { getLSItem } from 'util/localStorageProvider';

import QMASLogo from 'assets/img/branded-login/qmas-logo.svg';
import ICWLogo from 'assets/img/branded-login/icw-logo.svg';
import BcpLogo from 'assets/img/branded-login/brands/bcp.svg';
import BolLogo from 'assets/img/branded-login/brands/bol.svg';
import GoliathLogo from 'assets/img/branded-login/brands/goliath.svg';
import PRGLogo from 'assets/img/branded-login/brands/prg.svg';
import PreconLogo from 'assets/img/branded-login/brands/precon.svg';
import HendiLogo from 'assets/img/branded-login/brands/hendi.svg';
import VisibilityIcon from 'assets/img/branded-login/pwd-view-true.svg';
import VisibilityOffIcon from 'assets/img/branded-login/pwd-view-false.svg';

import classes from './BrandedLogin.module.scss';

// TODO: try to optimize => do not load from init
setLanguageLocale(T, [LOGIN_LANG_PATH, BRANDED_LOGIN_PATH]);

const MyTextField = ({ className, name, value, errorText, ...rest }) => {
  return (
    <TextField
      className={className}
      fullWidth
      storeName='general'
      name={name}
      value={value}
      margin='dense'
      error={!!errorText}
      helperText={errorText}
      {...rest}
    />
  );
};

const validateValues = {
  email: Yup.string()
    .required(T.translate('login.errors.emptyEmail'))
    .email(T.translate('login.errors.invalidEmailFormat')),
  password: Yup.string().required(T.translate('login.errors.emptyPassword')),
};

const initialValues = {
  email: '',
  password: '',
};

const BRANDS_DATA = {
  bcp: { logo: BcpLogo, title: 'Best Choice Product Ltd' },
  bol: { logo: BolLogo },
  goliath: { logo: GoliathLogo, title: 'Goliath Ltd.' },
  prg: { logo: PRGLogo },
  precon: { logo: PreconLogo },
  hendi: { logo: HendiLogo },
};

const BrandedLogin = (props) => {
  const { location, match, isAdmin, isUserLogin, roleId, loginLocked, onLogin } = props;

  const currentYear = moment().year();
  const lockedStorage = getLSItem('lockedLogin');
  const lockedTimeStorage = parseInt(getLSItem('lockedLoginTime') || 0, 10);
  const nowTime = new Date().getTime();
  const isLockedStorage =
    lockedStorage === 'true' && nowTime - lockedTimeStorage < LOCKED_LOGIN_TIME;

  const { logo: logoBrand, title: titleBrand } = get(
    BRANDS_DATA,
    `${get(match, 'params.brand')}`,
    BRANDS_DATA.bcp
  );

  const handleSubmitLogin = (values) => {
    if (isLockedStorage) return;

    onLogin(values.email, values.password);
  };

  const renderLoginForm = (formProps) => {
    const { handleSubmit, values, handleChange, errors, setFieldTouched, touched } = formProps;

    return (
      <form className={classes.loginForm} onSubmit={handleSubmit}>
        <Box className={classes.text}>
          <Typography className={classes.title}>{T.translate('brandedLogin.title')}</Typography>
          <Typography className={classes.subTitle}>
            {T.translate('brandedLogin.subTitle01')}
            <br />
            {T.translate('brandedLogin.subTitle02')}
          </Typography>
        </Box>
        <Box className={classes.form}>
          <MyTextField
            className={classes.email}
            name='email'
            value={values.email}
            errorText={touched.email && errors.email}
            onChange={handleChange}
            setFieldTouched={setFieldTouched}
            autoFocus
          />
          <MyTextField
            className={classes.password}
            name='password'
            type='password'
            value={values.password}
            errorText={touched.password && errors.password}
            onChange={handleChange}
            setFieldTouched={setFieldTouched}
            usingShowPassword
            visibilityIcons={{
              on: <img src={VisibilityIcon} alt='visibility on icon' />,
              off: <img src={VisibilityOffIcon} alt='visibility off icon' />,
            }}
          />
        </Box>
        {(loginLocked || isLockedStorage) && (
          <Box mt='24px'>
            <Typography variant='caption' display='block' align='left' color='error'>
              {T.translate('login.errors.locked.message')}
            </Typography>
            <Typography variant='caption' display='block' align='left' color='error'>
              {T.translate('login.errors.locked.tryAgain')}
            </Typography>
          </Box>
        )}
        <Button
          type='submit'
          variant='contained'
          fullWidth
          classes={{
            contained: classes.button,
          }}>
          {T.translate('general.login')}
        </Button>
      </form>
    );
  };

  const isUserLoginDOM = () => {
    const params = queryString.parse(location.search);
    const { action, redirect } = params;

    if (isAdmin) {
      if (roleId === 3) {
        nextPath2('/icw_admin');
      } else {
        nextPath2('/admin_panel');
      }
    } else if (redirect) {
      nextPath2(redirect);
    } else {
      if (action && action === 'fedex') {
        return <Redirect to={FEDEX_PATH} />;
      }
      return <Redirect to='/' />;
    }
  };

  if (isUserLogin) {
    return isUserLoginDOM();
  }

  return (
    <Box
      className={classes.wrapper}
      sx={{
        flexDirection: {
          xs: 'column',
          md: 'row',
        },
      }}>
      <Box className={cx(classes.section, classes.left)}>
        <Box className={classes.content}>
          <Form
            initialValues={initialValues}
            validateValues={validateValues}
            render={renderLoginForm}
            onSubmit={handleSubmitLogin}
          />
        </Box>
        <Box className={classes.footer}>
          <Typography>{`© ${currentYear} ${T.translate('brandedLogin.copyright')} ${T.translate(
            'brandedLogin.allRightReserved'
          )}`}</Typography>
        </Box>
      </Box>
      <Box className={cx(classes.section, classes.right)}>
        <Box className={classes.content}>
          {logoBrand && (
            <Box className={classes.brand}>
              <img className={classes.logo} src={logoBrand} alt='Brand logo' />
              {titleBrand && <Typography className={classes.title}>{titleBrand}</Typography>}
            </Box>
          )}
        </Box>
        <Box className={classes.footer}>
          <Box className={classes.poweredBy}>
            <Typography className={classes.text}>
              {T.translate('brandedLogin.poweredBy')}
            </Typography>
            <Box className={classes.logos}>
              <img
                src={QMASLogo}
                alt='QMAS logo'
                onClick={() => window.open('https://qmas.icw.io', '_blank')}
              />
              <img
                src={ICWLogo}
                alt='ICW logo'
                onClick={() => window.open('https://icw.io', '_blank')}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default BrandedLogin;
